// The admin's reconciliation screen for a company's Stripe Connect account.
// Six tabs read our own records (Summary, Payments, Refunds, Payouts,
// Disputes, Application fees) so an admin can check them against Stripe's
// own numbers. This file used to serve the restaurant too, behind a
// `viewer` prop; the restaurant's own equivalent now lives as its own set
// of pages under src/views/finance/pages/, and this file no longer branches
// on `viewer` at all — it is mounted from company-detail's Settlement tab
// only.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TWButton, TWTabNav } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import connectApi from '../../../services/connectApi';
import COPY from './connect-finance-copy';
import { DEFAULT_PRESET, resolvePeriod, toPeriodQuery } from './connect-period';
import ConnectPeriodPicker from './connect-period-picker';
import ConnectFinanceSummary from './connect-finance-summary';
import {
  ApplicationFeesTable,
  ConnectFinanceSection,
  DisputesTable,
  PaymentsTable,
  PayoutsTable,
  RefundsTable,
} from './connect-finance-tables';

// One reader per section. Each returns the ADAPTED value; a body the adapter
// rejects (null summary) is treated as a failed read so the card grid never
// paints zeros over an authorization error.
const READERS = {
  summary: async (companyId, { from, to }) => {
    const summary = connectApi.toFinanceSummary(
      await connectApi.getFinanceSummary(companyId, from, to),
    );
    if (!summary) throw new Error('summary');
    return summary;
  },
  payments: async (companyId, { from, to }) =>
    connectApi.toFinancePayments(await connectApi.getFinancePayments(companyId, from, to)),
  payouts: async (companyId, { from, to }) =>
    connectApi.toFinancePayouts(await connectApi.getFinancePayouts(companyId, from, to)),
  applicationFees: async (companyId, { from, to }) =>
    connectApi.toFinanceApplicationFees(
      await connectApi.getFinanceApplicationFees(companyId, from, to),
    ),
  // Refunds has no date filter on the API — the whole history is shown and
  // simply re-read alongside the others.
  refunds: async (companyId) => connectApi.toRefunds(await connectApi.getRefunds(companyId)),
  disputes: async (companyId, { from, to }) =>
    connectApi.toDisputes(await connectApi.getDisputes(companyId, from, to)),
};

// The six sections this screen reads from our own records. Reconciliation
// against Stripe's own numbers is the whole point of the admin view, so it
// keeps every reader, including applicationFees, whose buy-rate figures
// exist nowhere else and are never shown to a restaurant.
const SECTIONS = ['summary', 'payments', 'refunds', 'payouts', 'disputes', 'applicationFees'];

// The sub-navigation, in the order the owner settled on. Ids double as the
// keys they read out of `SECTIONS`/`READERS` above, so a tab never needs a
// mapping table of its own. Same list as SECTIONS: there is only one viewer
// now, so there is nothing left for a separate tab list to diverge on.
const TABS = SECTIONS;

// The query key the selected tab lives under.
const TAB_PARAM = 'tab';

const initialSections = (keys) =>
  keys.reduce((acc, key) => ({ ...acc, [key]: { data: null, error: '', loading: true } }), {});

const ConnectFinance = ({ companyId }) => {
  const copy = COPY.admin;
  const tabs = TABS.map((id) => ({ id, label: copy.sections[id].title }));

  // The selected tab lives in the URL rather than component state: a
  // refresh keeps it, and an admin can send someone a link straight to its
  // disputes. A missing or unrecognised value falls back to the first tab,
  // computed fresh on every render rather than written back — nothing here
  // needs the URL to carry an explicit "summary" the moment the page loads
  // with none at all.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get(TAB_PARAM);
  const activeTab = TABS.includes(requestedTab) ? requestedTab : TABS[0];
  const changeTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set(TAB_PARAM, id);
    // replace, not push: switching tabs is not a page the back button
    // should walk back through — it should leave the page instead.
    setSearchParams(next, { replace: true });
  };

  const [period, setPeriod] = useState({ preset: DEFAULT_PRESET, custom: { from: null, to: null } });
  const [sections, setSections] = useState(() => initialSections(SECTIONS));
  // Every load carries the request id current at the time it started; a
  // response that comes back after a newer load began is dropped.
  const requestId = useRef(0);
  // The query loadAll last computed, for a per-section retry to reuse —
  // retry never recomputes "now" on its own, it just re-reads the period
  // that is currently on screen. Null until the first loadAll runs, which
  // is also the earliest a Retry button can appear.
  const lastQueryRef = useRef(null);

  // Guards the two setSections calls below against a component that has
  // already unmounted by the time its read resolves (a real navigation, or
  // a test whose own assertion settles before every read of that render
  // has). requestId alone only protects against a *stale* answer; it says
  // nothing about whether anything is still mounted to receive this one.
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set here too, not only on cleanup: `useRef(true)`'s initial value only
    // covers the very first mount. If this effect ever re-runs without the
    // component fully unmounting first — StrictMode's mount/unmount/remount
    // rehearsal, or a future Offscreen-based cache — the previous cleanup
    // already left this false, and nothing set it back to true.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (key, id, query) => {
      setSections((prev) => ({ ...prev, [key]: { ...prev[key], loading: true, error: '' } }));
      try {
        const data = await READERS[key](companyId, query);
        if (!mountedRef.current || id !== requestId.current) return;
        setSections((prev) => ({ ...prev, [key]: { data, error: '', loading: false } }));
      } catch (error) {
        if (!mountedRef.current || id !== requestId.current) return;
        setSections((prev) => ({
          ...prev,
          [key]: { ...prev[key], error: copy.sections[key].error, loading: false },
        }));
      }
    },
    [companyId, copy],
  );

  const loadAll = useCallback(() => {
    // Computed here, not memoised on [period]: a dashboard left open across
    // midnight and Refreshed must ask for "now" as of the click, not as of
    // whichever render first resolved the period.
    const query = toPeriodQuery(resolvePeriod(period.preset, period.custom));
    lastQueryRef.current = query;
    requestId.current += 1;
    const id = requestId.current;
    SECTIONS.forEach((key) => load(key, id, query));
  }, [load, period]);

  useEffect(() => {
    if (!companyId) return;
    loadAll();
  }, [companyId, loadAll]);

  const retry = (key) => () => {
    // A single section retry must not invalidate the in-flight siblings;
    // it reuses the current request id and the query loadAll last used.
    load(key, requestId.current, lastQueryRef.current);
  };

  const summary = sections.summary;
  const payments = sections.payments;
  // Whether anything happened this period, from the one section every tab
  // set shares: the summary's own paymentCount/payoutCount already answer
  // this without needing a second copy of the payment rows.
  const nothingInPeriod =
    !summary.loading &&
    !summary.error &&
    !payments.loading &&
    !payments.error &&
    summary.data &&
    summary.data.paymentCount === 0 &&
    summary.data.payoutCount === 0 &&
    (payments.data || []).length === 0;

  // One case per tab id, each declaring two things together rather than one
  // and hoping the other stays in step with it: `periodScoped` — whether
  // what this tab is *about* to render actually reads a chosen period — and
  // the `content` itself, with that section's existing shell, loading,
  // empty and error handling untouched. Refunds is the one section without
  // a period concept: the API has no date filter for it, so its reader
  // ignores {from, to} and its picker never shows.
  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return {
          periodScoped: true,
          content: (
            <>
              {summary.error ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-900/50 dark:bg-rose-900/20">
                  <p className="text-sm text-rose-700 dark:text-rose-300">{summary.error}</p>
                  <TWButton
                    size="sm"
                    label={copy.retry}
                    onClick={retry('summary')}
                    aria-label={copy.retry + ' summary'}
                  />
                </div>
              ) : (
                <ConnectFinanceSummary summary={summary.data} viewer="admin" loading={summary.loading} />
              )}

              {nothingInPeriod ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{copy.emptyPeriod}</p>
              ) : null}
            </>
          ),
        };

      case 'payments':
        return {
          periodScoped: true,
          content: (
            <ConnectFinanceSection
              title={copy.sections.payments.title}
              description={copy.sections.payments.description}
              error={payments.error}
              loading={payments.loading && !payments.data}
              onRetry={retry('payments')}
              retryLabel={copy.retry}
            >
              <PaymentsTable rows={payments.data || []} viewer="admin" empty={copy.sections.payments.empty} />
            </ConnectFinanceSection>
          ),
        };

      case 'refunds':
        // The API predates a date filter — every refund is read every time,
        // newest first, regardless of what the picker says — so this stays
        // unscoped.
        return {
          periodScoped: false,
          content: (
            <ConnectFinanceSection
              title={copy.sections.refunds.title}
              description={copy.sections.refunds.description}
              error={sections.refunds.error}
              loading={sections.refunds.loading && !sections.refunds.data}
              onRetry={retry('refunds')}
              retryLabel={copy.retry}
            >
              <RefundsTable rows={sections.refunds.data || []} empty={copy.sections.refunds.empty} />
            </ConnectFinanceSection>
          ),
        };

      case 'disputes':
        return {
          // Our own table: READERS.disputes takes {from, to}, so the picker
          // genuinely governs it here.
          periodScoped: true,
          content: (
            <ConnectFinanceSection
              title={copy.sections.disputes.title}
              description={copy.sections.disputes.description}
              error={sections.disputes.error}
              loading={sections.disputes.loading && !sections.disputes.data}
              onRetry={retry('disputes')}
              retryLabel={copy.retry}
              note={copy.disputeNote}
            >
              <DisputesTable rows={sections.disputes.data || []} empty={copy.sections.disputes.empty} />
            </ConnectFinanceSection>
          ),
        };

      case 'payouts':
        return {
          // Our own table: READERS.payouts takes {from, to}, same as disputes.
          periodScoped: true,
          content: (
            <ConnectFinanceSection
              title={copy.sections.payouts.title}
              description={copy.sections.payouts.description}
              error={sections.payouts.error}
              loading={sections.payouts.loading && !sections.payouts.data}
              onRetry={retry('payouts')}
              retryLabel={copy.retry}
            >
              <PayoutsTable rows={sections.payouts.data || []} empty={copy.sections.payouts.empty} />
            </ConnectFinanceSection>
          ),
        };

      case 'applicationFees':
        return {
          periodScoped: true,
          content: (
            <ConnectFinanceSection
              title={copy.sections.applicationFees.title}
              description={copy.sections.applicationFees.description}
              error={sections.applicationFees.error}
              loading={sections.applicationFees.loading && !sections.applicationFees.data}
              onRetry={retry('applicationFees')}
              retryLabel={copy.retry}
            >
              <ApplicationFeesTable
                rows={sections.applicationFees.data || []}
                empty={copy.sections.applicationFees.empty}
              />
            </ConnectFinanceSection>
          ),
        };

      default:
        return { periodScoped: false, content: null };
    }
  };

  // `showPicker` and the tab's own content come from the same call, on
  // purpose — see renderTab's own comment above.
  const { periodScoped: showPicker, content: tabContent } = renderTab();

  return (
    <div className="space-y-6">
      <div
        className={cn(
          'flex flex-col gap-3 sm:flex-row sm:items-center',
          showPicker ? 'sm:justify-between' : 'sm:justify-end',
        )}
      >
        {showPicker ? (
          <ConnectPeriodPicker preset={period.preset} custom={period.custom} onChange={setPeriod} />
        ) : null}
        <TWButton size="sm" icon={ArrowPathIcon} label={copy.refresh} onClick={loadAll} />
      </div>

      <TWTabNav tabs={tabs} value={activeTab} onChange={changeTab} ariaLabel={copy.tabsAriaLabel} />

      {tabContent}
    </div>
  );
};

export default ConnectFinance;
